import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  getVehicleDealers,
  VehicleDealerListFilters,
} from "../../../api/quote/getVehicleDealers";
import APButton from "../../../components/buttons/APButton";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../../components/form/APAutoComplete";
import APForm from "../../../components/form/APForm";
import APTextField from "../../../components/form/APTextField";
import PaginationContainer from "../../../components/PaginationContainer";
import { State, stateOptions } from "../../../types/State";
import {
  VehicleDealer,
  VehicleDealerRating,
  vehicleDealerRatingOptions,
} from "../../../types/VehicleDealer";
import VehicleDealerCard from "./VehicleDealerCard";

type VehicleDealerSearchFilters = {
  rating: AutoCompleteOption | null;
  state: AutoCompleteOption | null;
  nameSearch: string;
};

type VehicleDealerSearchActions = {
  onToggleSelectDealer: (vehicleDealer: VehicleDealer) => Promise<void>;
};

const VehicleDealerSearch = ({
  initialFilters,
  actions,
  selectedDealers,
  crmClientURL,
  receivedDealerIds,
}: {
  initialFilters: VehicleDealerListFilters;
  actions: VehicleDealerSearchActions;
  selectedDealers: VehicleDealer[];
  crmClientURL: string | null;
  receivedDealerIds: string[];
}) => {
  const [paginationHistory, setPaginationHistory] = useState<string[]>([]);
  const [filters, setFilters] =
    useState<VehicleDealerListFilters>(initialFilters);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VehicleDealerSearchFilters>({
    mode: "onTouched",
  });

  const dealers = getVehicleDealers(filters, {
    keepPreviousData: true,
  });

  const handlePageChange = (
    nextPageLink: string | undefined,
    paginationHistory: string[]
  ) => {
    setPaginationHistory(paginationHistory);
    setFilters({ ...filters, nextPageLink });
  };

  const handleSubmitFilters = (inputs: VehicleDealerSearchFilters) => {
    setFilters({
      pageSize: initialFilters.pageSize,
      make: initialFilters.make,
      state: inputs.state ? (inputs.state.id as State) : null,
      rating:
        inputs.rating && inputs.rating.id !== "all"
          ? (inputs.rating.id as VehicleDealerRating)
          : null,
      nameSearch: inputs.nameSearch,
      nextPageLink: undefined,
    });
    setPaginationHistory([]);
  };

  const renderItems = (items: VehicleDealer[]) => {
    const dealerIsSelected = (dealer: VehicleDealer) => {
      return selectedDealers.some((d) => d.id === dealer.id);
    };

    return items.map((dealer) => (
      <VehicleDealerCard
        key={dealer.id}
        vehicleDealer={dealer}
        actions={{
          onToggleSelect: actions.onToggleSelectDealer,
        }}
        config={{
          isSelected: dealerIsSelected(dealer),
          hasSentRequest: receivedDealerIds.includes(dealer.id),
          crmClientURL: crmClientURL,
        }}
      />
    ));
  };

  return (
    <Box>
      <APForm
        onSubmit={handleSubmit(handleSubmitFilters)}
        submitText={"Search"}
        isLoading={false}
        isError={Object.keys(errors).length > 0}
        minSpacing
        submitButtonColor="primary"
        hideSubmitButton
      >
        <Stack
          spacing={0}
          sx={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: { md: "1fr 1fr" },
          }}
        >
          <APAutoComplete
            name="state"
            label="State"
            options={stateOptions}
            validations={{}}
            control={control}
            errors={errors}
            defaultValue={
              filters.state
                ? stateOptions.find((option) => option.id === filters.state)
                : null
            }
          />
          <APAutoComplete
            name="rating"
            label="Rating"
            options={vehicleDealerRatingOptions}
            validations={{}}
            control={control}
            errors={errors}
            defaultValue={
              filters.rating
                ? vehicleDealerRatingOptions.find(
                    (option) => option.id === filters.rating
                  )
                : null
            }
          />
          <APTextField
            name="nameSearch"
            label="Name Search"
            validations={{}}
            control={control}
            errors={errors}
            defaultValue=""
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              alignItems: "center",
            }}
          >
            <APButton
              variant="contained"
              text="Search"
              color="primary"
              onClick={handleSubmit(handleSubmitFilters)}
            />
          </Box>
        </Stack>
      </APForm>
      <PaginationContainer
        paginationHistory={paginationHistory}
        currentPage={paginationHistory.length + 1}
        pageSize={initialFilters.pageSize}
        itemCount={dealers.data?.count || 0}
        onPageChange={handlePageChange}
        nextPageLink={dealers.data?.nextPageLink || ""}
        isLoading={dealers.isFetching}
      >
        <Stack
          spacing={0}
          sx={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: { md: "1fr 1fr" },
          }}
        >
          {renderItems(dealers.data?.items || [])}
        </Stack>
      </PaginationContainer>
    </Box>
  );
};

export default VehicleDealerSearch;
